import { StringObject } from "../../../objects/StringObject";
import { makeBadge } from "./StringDevElements";
import {
  OverlayBaseMetrics,
  StringDevOverlayBadgeState,
  StringDevOverlayModule,
} from "./StringDevOverlayModule";
import { StringDevModule } from "./StringDevModule";

const BADGE_SLOT_STEP = 31;

export type StringDevBadgeSlot = "top-left" | "top-right" | "bottom-right" | "bottom-left";

export interface StringDevBadgeSlotConfig {
  gap?: number;
  offsetX?: number;
  offsetY?: number;
}

export interface StringDevBadgeDescriptor {
  id: string;
  slot?: StringDevBadgeSlot;
  group?: string;
  selectorAttribute?: string;
  depth?: number;
  attributes?: Record<string, string | boolean | number | null | undefined>;
  gap?: number;
  offsetX?: number;
  offsetY?: number;
}

export interface StringDevManagedBadge {
  id: string;
  descriptor: StringDevBadgeDescriptor;
  element: HTMLButtonElement;
}

export interface StringDevBadgePositionMeasurement {
  visible: boolean;
  docX: number;
  docY: number;
  translate?: string;
}

export interface StringDevBadgeMeasurement {
  visible: boolean;
  badges: Record<string, StringDevBadgePositionMeasurement>;
}

export interface StringDevBadgeEntry<TExtra = undefined> {
  object: StringObject;
  targetId: string;
  badge: HTMLButtonElement;
  depth: number;
  extra: TExtra;
  cleanup: Array<() => void>;
  badges: Map<string, StringDevManagedBadge>;
  groups: Map<string, HTMLDivElement>;
}

export abstract class StringDevBadgeOverlayModule<
  TExtra = undefined,
  TMeasurement extends StringDevBadgeMeasurement = StringDevBadgeMeasurement,
> extends StringDevOverlayModule<StringDevBadgeEntry<TExtra>, TMeasurement> {
  protected get badgeAttribute(): string {
    return (this.constructor as typeof StringDevModule).devtool?.overlay?.badgeAttribute ?? "";
  }

  protected createOverlayEntry(object: StringObject): StringDevBadgeEntry<TExtra> {
    const targetId = this.resolveOverlayTargetId(object);
    const depth = this.resolveBadgeDepth(object);
    const cleanup: Array<() => void> = [];
    const entry: StringDevBadgeEntry<TExtra> = {
      object,
      targetId,
      badge: document.createElement("button"),
      depth,
      extra: undefined as TExtra,
      cleanup,
      badges: new Map(),
      groups: new Map(),
    };

    const descriptors = this.getBadgeDescriptors(object, targetId, depth);
    const world = this.badgeLayer.getWorldElement(this.getWorldHost());

    for (const descriptor of descriptors) {
      const badge = this.createOverlayBadge({
        targetId,
        selectorAttribute: descriptor.selectorAttribute,
        depth: descriptor.depth ?? depth,
        attributes: {
          "data-badge-id": descriptor.id,
          ...descriptor.attributes,
        },
      });
      const managedBadge: StringDevManagedBadge = {
        id: descriptor.id,
        descriptor,
        element: badge,
      };
      badge.setAttribute("data-visible", "false");

      const onClick = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        this.onBadgeClick(entry, event, managedBadge);
      };

      badge.addEventListener("click", onClick);
      cleanup.push(() => badge.removeEventListener("click", onClick));
      entry.badges.set(descriptor.id, managedBadge);

      if (descriptor.group) {
        let groupEl = entry.groups.get(descriptor.group);
        if (!groupEl) {
          groupEl = makeBadge(targetId);
          entry.groups.set(descriptor.group, groupEl);
          world.appendChild(groupEl);
        }
        groupEl.appendChild(badge);
      } else {
        world.appendChild(badge);
      }
    }

    const primaryBadge = entry.badges.values().next().value as StringDevManagedBadge | undefined;
    if (!primaryBadge) {
      throw new Error(`${this.constructor.name} must define at least one badge descriptor.`);
    }

    entry.badge = primaryBadge.element;
    entry.extra = this.createBadgeExtra(object, entry.badge, targetId, depth);

    const subscriptions = this.bindBadge(entry);
    if (subscriptions) {
      cleanup.push(...subscriptions);
    }

    this.applyBadgeEnabledState(entry);
    this.renderBadge(entry);

    return entry;
  }

  protected destroyOverlayEntry(entry: StringDevBadgeEntry<TExtra>): void {
    for (const cleanup of entry.cleanup) {
      cleanup();
    }
    this.destroyBadgeExtra(entry);
    for (const group of entry.groups.values()) {
      group.remove();
    }
    for (const badge of entry.badges.values()) {
      if (!badge.descriptor.group) {
        badge.element.remove();
      }
    }
  }

  protected measureEntry(
    entry: StringDevBadgeEntry<TExtra>,
    metrics: OverlayBaseMetrics,
  ): TMeasurement {
    const measurements: Record<string, StringDevBadgePositionMeasurement> = {};

    if (!metrics.visible) {
      for (const badge of entry.badges.values()) {
        measurements[badge.id] = { visible: false, docX: 0, docY: 0 };
      }
      return { visible: false, badges: measurements } as TMeasurement;
    }

    // Build ordered slot items: ungrouped badges and groups each get one slot index.
    // Key: either badge.id (ungrouped) or "group:<groupId>" (grouped).
    type SlotItem =
      | { kind: "badge"; badge: StringDevManagedBadge }
      | { kind: "group"; groupId: string; badges: StringDevManagedBadge[] };

    const slotItems = new Map<StringDevBadgeSlot, SlotItem[]>();

    const seenGroups = new Set<string>();
    for (const badge of entry.badges.values()) {
      const slot = badge.descriptor.slot ?? "top-left";
      if (!slotItems.has(slot)) slotItems.set(slot, []);
      const items = slotItems.get(slot)!;

      if (badge.descriptor.group) {
        const groupId = badge.descriptor.group;
        if (!seenGroups.has(groupId)) {
          seenGroups.add(groupId);
          items.push({ kind: "group", groupId, badges: [] });
        }
        const groupItem = items.find(
          (i): i is Extract<SlotItem, { kind: "group" }> =>
            i.kind === "group" && i.groupId === groupId,
        );
        groupItem?.badges.push(badge);
      } else {
        items.push({ kind: "badge", badge });
      }
    }

    for (const [slot, items] of slotItems) {
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        const representativeBadge = item.kind === "badge" ? item.badge : item.badges[0];
        const slotConfig = this.resolveBadgeSlotConfig(representativeBadge.descriptor);
        const step = BADGE_SLOT_STEP + (slotConfig.gap ?? 0);
        const offsetX = slotConfig.offsetX ?? 0;
        const offsetY = slotConfig.offsetY ?? 0;
        const position = this.resolveBadgePosition(metrics, slot, index, step, offsetX, offsetY);

        if (item.kind === "badge") {
          measurements[item.badge.id] = position;
        } else {
          // All badges in the group share the same slot position —
          // the wrapper gets the transform, individual buttons don't need coordinates.
          for (const badge of item.badges) {
            measurements[badge.id] = position;
          }
        }
      }
    }

    return { visible: true, badges: measurements } as TMeasurement;
  }

  protected applyMeasurement(
    entry: StringDevBadgeEntry<TExtra>,
    measurement: TMeasurement | undefined,
  ): void {
    // Apply position to group wrappers (use first badge in group for measurement).
    const positionedGroups = new Set<string>();
    for (const badge of entry.badges.values()) {
      const groupId = badge.descriptor.group;
      if (!groupId) continue;
      if (positionedGroups.has(groupId)) continue;
      positionedGroups.add(groupId);

      const groupEl = entry.groups.get(groupId);
      if (groupEl) {
        this.applyOverlayBadgePosition(groupEl, measurement?.badges[badge.id]);
      }
    }

    // Apply visibility to individual badges; ungrouped badges also get their position.
    for (const badge of entry.badges.values()) {
      if (badge.descriptor.group) {
        const pos = measurement?.badges[badge.id];
        badge.element.setAttribute("data-visible", pos?.visible ? "true" : "false");
      } else {
        this.applyOverlayBadgePosition(badge.element, measurement?.badges[badge.id]);
      }
    }

    this.afterBadgeMeasurement(entry, measurement);
  }

  protected onEnabledChange(_enabled: boolean): void {
    for (const entry of this.entries.values()) {
      this.applyBadgeEnabledState(entry);
      this.renderBadge(entry);
    }
  }

  protected createBadgeExtra(
    _object: StringObject,
    _badge: HTMLButtonElement,
    _targetId: string,
    _depth: number,
  ): TExtra {
    return undefined as TExtra;
  }

  protected destroyBadgeExtra(_entry: StringDevBadgeEntry<TExtra>): void {}

  protected bindBadge(_entry: StringDevBadgeEntry<TExtra>): Array<() => void> | void {}

  protected resolveBadgeDepth(object: StringObject): number {
    return this.resolveOverlayObjectDepth(object.htmlElement);
  }

  protected getBadgeSlotConfig(): Partial<Record<StringDevBadgeSlot, StringDevBadgeSlotConfig>> {
    return {};
  }

  protected getBadgeDescriptors(
    object: StringObject,
    targetId: string,
    depth: number,
  ): Array<StringDevBadgeDescriptor> {
    return [
      {
        id: "primary",
        slot: "top-left",
        selectorAttribute: this.badgeAttribute,
        attributes: this.getInitialBadgeAttributes(object, targetId, depth),
      },
    ];
  }

  protected getInitialBadgeAttributes(
    _object: StringObject,
    _targetId: string,
    _depth: number,
  ): Record<string, string | boolean | number | null | undefined> | undefined {
    return undefined;
  }

  protected applyBadgeEnabledState(entry: StringDevBadgeEntry<TExtra>): void {
    for (const badge of entry.badges.values()) {
      this.applyOverlayBadgeState(badge.element, {
        disabled: !this.enabled,
        attributes: { "data-module-enabled": this.enabled },
      });
    }
    for (const group of entry.groups.values()) {
      group.setAttribute("data-module-enabled", this.enabled ? "true" : "false");
    }
  }

  protected renderBadge(entry: StringDevBadgeEntry<TExtra>): void {
    for (const badge of entry.badges.values()) {
      this.applyOverlayBadgeState(badge.element, this.getBadgeState(entry, badge));
    }
  }

  protected afterBadgeMeasurement(
    _entry: StringDevBadgeEntry<TExtra>,
    _measurement: TMeasurement | undefined,
  ): void {}

  protected abstract onBadgeClick(
    entry: StringDevBadgeEntry<TExtra>,
    event: MouseEvent,
    badge: StringDevManagedBadge,
  ): void;

  protected abstract getBadgeState(
    entry: StringDevBadgeEntry<TExtra>,
    badge?: StringDevManagedBadge,
  ): StringDevOverlayBadgeState;

  private resolveBadgeSlotConfig(descriptor: StringDevBadgeDescriptor): StringDevBadgeSlotConfig {
    const slot = descriptor.slot ?? "top-left";
    const shared = this.getBadgeSlotConfig()[slot] ?? {};
    return {
      gap: descriptor.gap ?? shared.gap ?? 0,
      offsetX: descriptor.offsetX ?? shared.offsetX ?? 0,
      offsetY: descriptor.offsetY ?? shared.offsetY ?? 0,
    };
  }

  private resolveBadgePosition(
    metrics: OverlayBaseMetrics,
    slot: StringDevBadgeSlot,
    index: number,
    step: number,
    offsetX: number,
    offsetY: number,
  ): StringDevBadgePositionMeasurement {
    const anchorOffsetX = metrics.baseAnchorX - metrics.contentX;
    const anchorOffsetY = metrics.baseAnchorY - metrics.contentY + metrics.collisionOffset;
    const left = metrics.contentX + anchorOffsetX;
    const right = metrics.contentX + metrics.width + anchorOffsetX;
    const top = metrics.contentY + anchorOffsetY;
    const bottom = metrics.contentY + metrics.height + anchorOffsetY;
    switch (slot) {
      case "top-right":
        return {
          visible: true,
          docX: right - index * step + offsetX,
          docY: top + offsetY,
          translate: "translate(-100%, 0%)",
        };
      case "bottom-right":
        return {
          visible: true,
          docX: right - index * step + offsetX,
          docY: bottom + offsetY,
          translate: "translate(-100%, -100%)",
        };
      case "bottom-left":
        return {
          visible: true,
          docX: left + index * step + offsetX,
          docY: bottom + offsetY,
          translate: "translate(0%, -100%)",
        };
      case "top-left":
      default:
        return {
          visible: true,
          docX: left + index * step + offsetX,
          docY: top + offsetY,
          translate: "translate(0%, 0%)",
        };
    }
  }
}
